import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Done, Clear } from '@mui/icons-material';
import { API } from '../../services/amplifyCompat';
import { getAllUnsafeSubmittedActionsForAdmin } from '../../graphql/queries';
import { overrideUnsafeSubmittedAction } from '../../graphql/mutations';
import { useAuthenticatedMediaUrl } from '../../hooks/use-authenticated-media-url';
import useTranslation from '../customHooks/translations';

const FlaggedImageCard = ({ action, onOverride }) => {
  const translation = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittedImageUrl = useAuthenticatedMediaUrl(action.submitted_image);

  const overrideAction = async (allow) => {
    setIsSubmitting(true);
    try {
      await API.graphql({
        query: overrideUnsafeSubmittedAction,
        variables: { sa_id: action.sa_id, allow },
      });
      await onOverride();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card sx={{ display: 'flex', overflow: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          alignItems: { xs: 'center', md: 'flex-start' },
        }}
      >
        {submittedImageUrl && (
          <CardMedia
            component="img"
            sx={{ width: 150 }}
            image={submittedImageUrl}
            alt={`A flagged ${action.action_name} action image submitted at ${action.time_submitted}`}
          />
        )}
        <CardContent sx={{ ml: { xs: '0em', md: '2em' }, flexGrow: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: '1.5em' }}>
            {action.date_of_action?.split('T')[0]}{' '}
            {action.name_of_user ? `- ${action.name_of_user}` : ''}
          </Typography>
          <Typography variant="h2">{action.action_name}</Typography>
          <Typography sx={{ my: 1.5, color: '#7e7e7e' }}>
            {action.submitted_action_items}
          </Typography>
          <Typography variant="body2" sx={{ color: '#7e7e7e' }}>
            {action.image_validation_reason}
          </Typography>
        </CardContent>
        <Stack
          direction={{ xs: 'row', md: 'column' }}
          spacing={1.5}
          sx={{ alignSelf: 'center', m: '1em' }}
        >
          <Button
            variant="outlined"
            startIcon={<Done />}
            onClick={() => overrideAction(true)}
            disabled={isSubmitting}
          >
            {translation.flaggedSendToReview}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Clear />}
            onClick={() => overrideAction(false)}
            disabled={isSubmitting}
          >
            {translation.flaggedReject}
          </Button>
        </Stack>
      </Box>
    </Card>
  );
};

const FlaggedImagesPanel = () => {
  const translation = useTranslation();
  const [flaggedActions, setFlaggedActions] = useState();

  const getFlaggedActions = useCallback(async () => {
    const res = await API.graphql({
      query: getAllUnsafeSubmittedActionsForAdmin,
    });
    setFlaggedActions(res.data.getAllUnsafeSubmittedActionsForAdmin);
  }, []);

  useEffect(() => {
    getFlaggedActions();
  }, [getFlaggedActions]);

  if (!flaggedActions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '2em' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (flaggedActions.length === 0) {
    return (
      <Typography sx={{ textAlign: 'center', mt: '2em' }}>
        {translation.flaggedImagesEmpty}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {flaggedActions.map((action) => (
        <FlaggedImageCard
          key={action.sa_id}
          action={action}
          onOverride={getFlaggedActions}
        />
      ))}
    </Stack>
  );
};

export default FlaggedImagesPanel;
